import React from 'react';
import { useIsConsoleAvailable } from '@hotjar/recordings/src/features/RecordingsPage/hooks/useIsConsoleAvailable';

import { isRecordingFromProductLink } from '../../utils/isRecordingFromProductLink';
import { EventMarkerElement } from './EventMarkerElement';
import { EventMarkerTooltip } from './EventMarkerTooltip';

import type { EventMarker } from '../../types';

interface Props {
  eventMarkers: EventMarker[];
  width: number;
  isDesktopRecording: boolean;
  siteId: number;
  onMouseDown: (clientX: number, uiPosition: string) => void;
  setIsHoveringOneMarker: (state: boolean) => void;
  firstFilterMatch: EventMarker | null;
}

/**
 * Hard cap on shown error events per recording.
 * This might cause confusion on recordings with many errors and should be improved in the future.
 * @see RECS-1434
 */
export const ERROR_EVENT_LIMIT = 20;

export const filterEventMarkers = (
  eventMarkers: EventMarker[],
  isConsoleAvailable: boolean,
): EventMarker[] => {
  let errorCount = 0;

  return eventMarkers.reduce((markers, marker) => {
    if (marker.progress_pos === markers[markers.length - 1]?.progress_pos) {
      return markers;
    }

    if (marker.type === 'error') {
      if (!isConsoleAvailable || errorCount >= ERROR_EVENT_LIMIT) return markers;
      errorCount += 1;
    }

    return [...markers, marker];
  }, [] as EventMarker[]);
};

const EventMarkerListUnmemo = ({
  eventMarkers,
  width,
  isDesktopRecording,
  siteId,
  onMouseDown,
  setIsHoveringOneMarker,
  firstFilterMatch,
}: Props): JSX.Element | null => {
  const showFilterMatchTooltip = !isRecordingFromProductLink();
  const isConsoleAvailable = useIsConsoleAvailable();

  if (!width) {
    return null;
  }

  return (
    <>
      {filterEventMarkers(eventMarkers, isConsoleAvailable).map((eventMarker) => (
        <EventMarkerTooltip
          eventMarker={eventMarker}
          isDesktopRecording={isDesktopRecording}
          siteId={siteId}
          key={eventMarker.key}
          setIsHoveringMarker={setIsHoveringOneMarker}
        >
          <EventMarkerElement
            eventMarker={eventMarker}
            xPositionInPx={(+eventMarker.progress_pos / 100) * width}
            isDesktopRecording={isDesktopRecording}
            onMouseDown={onMouseDown}
            setIsHoveringMarker={setIsHoveringOneMarker}
            showFilterMatchTooltip={showFilterMatchTooltip}
            firstFilterMatch={firstFilterMatch}
          />
        </EventMarkerTooltip>
      ))}
    </>
  );
};

export const EventMarkerList = React.memo(EventMarkerListUnmemo);
